/**
 * The view's print guide finish (owner, 2026-10-15). **Flatten the magenta** paints the guide
 * flat pure magenta outside the camera islands. **Fix camera shading** takes the shading back
 * from the original AI image over each camera island, widened by the area the operator sets; at
 * 100 % it covers the whole guide, so a guide the cloud flattened gets all its detail back.
 */
import { useEffect, useId, useState } from "react";
import type { WireShadingFix } from "@genz/engine-bind";
import { DEFAULT_ISLAND_MARGIN } from "../cameraShading.js";

const MAX_MARGIN_PCT = 100;
/** A slider drag commits once it rests, so the editor re-keys once, not on every step. */
const COMMIT_MS = 400;

export interface CameraShadingFixProps {
  locked?: boolean;
  /** The original AI image of this view can be found (a Generate job made it). */
  available: boolean;
  fix: WireShadingFix | null;
  /** The view is painted flat pure magenta outside the camera islands. */
  flatten: boolean;
  onFlatten: (flatten: boolean) => void;
  busy: boolean;
  onEnable: () => void;
  onMargin: (margin: number) => void;
  onRemove: () => void;
}

export function CameraShadingFix({
  locked, available, fix, flatten, onFlatten, busy, onEnable, onMargin, onRemove,
}: CameraShadingFixProps) {
  const sliderId = useId();
  const flattenId = useId();
  const [pct, setPct] = useState(Math.round((fix?.margin ?? DEFAULT_ISLAND_MARGIN) * 1000) / 10);
  useEffect(() => {
    if (fix) setPct(Math.round(fix.margin * 1000) / 10);
  }, [fix]);
  useEffect(() => {
    if (!fix || Math.abs(pct / 100 - fix.margin) < 1e-6) return;
    const t = setTimeout(() => onMargin(pct / 100), COMMIT_MS);
    return () => clearTimeout(t);
  }, [pct, fix, onMargin]);

  const flattenRow = (
    <div className="mask-editor__chroma-row" data-testid="flatten-magenta-row">
      <label className="mask-editor__chroma-tol" htmlFor={flattenId}
        title="Paint this view's print guide flat pure magenta; the camera lip keeps its shading">
        <input
          id={flattenId}
          type="checkbox"
          aria-label="Flatten the magenta"
          checked={flatten}
          onChange={(e) => onFlatten(e.target.checked)}
          disabled={locked || busy}
          data-testid="editor-flatten-magenta"
        />{" "}
        Flatten the magenta
      </label>
    </div>
  );
  if (!fix && !available) return flattenRow;
  return (
    <>
    {flattenRow}
    <div className="mask-editor__chroma-row" data-testid="camera-shading-fix">
      <span className="mask-editor__marker-current">Camera shading</span>
      {fix ? (
        <>
          <label className="mask-editor__chroma-tol" htmlFor={sliderId}
            title="How far past the camera openings the original AI shading comes back">
            Shading area {pct}%
            <input
              id={sliderId}
              type="range"
              min={0}
              max={MAX_MARGIN_PCT}
              step={0.5}
              value={pct}
              onChange={(e) => setPct(Number(e.target.value))}
              disabled={locked || busy}
              aria-label="Camera shading area"
              data-testid="camera-shading-area"
            />
          </label>
          <button type="button" className="btn btn--sm btn--ghost" onClick={onRemove} disabled={locked || busy}
            data-testid="camera-shading-remove">
            Remove fix
          </button>
        </>
      ) : (
        <button type="button" className="btn btn--sm" onClick={onEnable} disabled={locked || busy}
          title="Take the shading around the camera back from the original AI image"
          data-testid="camera-shading-enable">
          {busy ? "Fixing…" : "Fix camera shading"}
        </button>
      )}
      <span className="faint mask-editor__hint">
        If the shading around the camera is cut, take it back from the original AI image; 100% brings
        back all of it. Save to keep it.
      </span>
    </div>
    </>
  );
}
